import React, { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

import { apiGetPrepaidCompetitionReconciliation,
         apiStartPrepaidCompetitionReconciliation } from './Api';
import { CourierRun } from './Courier';
import { reportError } from './CallCenter';
import { ReconciliationStatus, ClubEvidence } from './Types';
import { useHasCapability } from './Capabilities';
import { FocusClub } from './PrepaidServices';
import formatCurrency from './Formatting';

// What the run is doing, in the order it does it. Reading a few hundred pages through the user's
// own browser takes minutes, and a bare spinner for that long is indistinguishable from a hang.
const PHASE: Record<string, string> = {
    events: 'Söker vilken tävling betalningsomgången hör till',
    clubs:  'Läser klubbarnas anmälningar',
    orders: 'Läser fakturorna',
    verify: 'Kontrollerar avgifter på lagsidorna',
}

type Progress = { phase: string, done: number, total: number }

// Often enough to feel live, rarely enough to be nothing next to what the run itself
// is doing to Eventor.
const POLL_MS = 2000

// Findings from three different rules are shown in one list, deliberately. A deadline moved later
// reprices teams that already exist, and shows up as the same club appearing twice — too much at one
// price, too little at another. Saying so would mean asserting a cause the data does not carry, so
// the two halves are shown and the note at the foot explains the shape once.
const KIND = {
    over:  { label: 'För mycket fakturerat', color: '#c62828' },
    under: { label: 'För lite fakturerat',   color: '#1565c0' },
    group: { label: 'Oklart mellan klubbar', color: '#ed6c02' },
}

type Finding = {
    kind: keyof typeof KIND, clubs: string[], unit_price_ore: number, count: number, detail: string,
}

const ore = (v: number) => formatCurrency(v / 100)

/** When the report was made. A found-again report says nothing about its own age otherwise. */
function when(iso: string | null): string | null {
    if (iso === null || iso === '') return null
    const at = new Date(iso)
    return Number.isNaN(at.getTime())
        ? null : at.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })
}

/** The club to open in Tjänster for a finding, when there is exactly one and it holds services.
 *
 * Named as well as identified: the name is right here, and the view being opened would otherwise
 * have to fetch the whole club list through the user's browser to learn what to call it.
 */
function servicesOf(evidence: ClubEvidence[], finding: Finding): FocusClub | null {
    if (finding.clubs.length !== 1) return null
    const club = evidence.find(e => e.club === finding.clubs[0])
    if (club === undefined || club.organisation_id === null) return null
    return { id: club.organisation_id, name: club.club }
}

function PrepaidCompetitionReconciliation({ paymentEventId, courier, running, setRunning, onCorrectClub }: {
    paymentEventId: number | null,
    courier: CourierRun,
    // Hand a club to the Tjänster tab and go there. The report is one route in, not the trigger.
    onCorrectClub: (club: FocusClub) => void,
    // What the courier is busy with, if anything. Shared with the view beside this one: a person
    // has one tab and one job at a time, so starting something here would end whatever is running
    // there, and the buttons say so rather than discovering it the hard way.
    running: string | null,
    setRunning: (what: string | null) => void,
}) {
    const canCorrect = useHasCapability('eventor.fix_registrations')
    const [data, setData] = useState<ReconciliationStatus | null>(null)
    const [loading, setLoading] = useState(false)
    const [progress, setProgress] = useState<Progress | null>(null)
    // Kept here as well as on the error bus. A run costs minutes of the user's browser, and the one
    // thing they need when it fails is what went wrong — not a message that has already gone.
    const [failure, setFailure] = useState<string | null>(null)

    // Ask how it is going until it stops going. Each request is short, so nothing between the
    // browser and Eko has an opinion about it — which is what a four-minute connection could not
    // manage: it was lost to a proxy timeout, a page reload and a backgrounded tab on the same day.
    const follow = async (runId: string) => {
        setLoading(true)
        setRunning('Avstämning')
        try {
            for (;;) {
                const status = await apiGetPrepaidCompetitionReconciliation(runId)
                if (status.state === 'running') {
                    setProgress({ phase: status.phase, done: status.done, total: status.total })
                    await new Promise(resolve => setTimeout(resolve, POLL_MS))
                    continue
                }
                if (status.state === 'done') {
                    setData(status)
                } else if (status.state === 'failed') {
                    const message = status.message ?? 'Avstämningen misslyckades.'
                    setFailure(message)
                    reportError({ status: 502, code: 'reconciliation_failed', message })
                } else {
                    setFailure('Avstämningen kunde inte hittas — den kan ha rensats bort.')
                }
                return
            }
        } finally {
            setLoading(false)
            setProgress(null)
            setRunning(null)
        }
    }

    // A run started before this page existed — after a reload, or in another tab — is either still
    // going or already finished. Picking it up costs one request and saves starting the whole thing
    // over, which is minutes of the user's browser fetching from Eventor.
    useEffect(() => {
        void apiGetPrepaidCompetitionReconciliation('latest').then(status => {
            if (status.state === 'done') {
                setData(status)
            } else if (status.state === 'running') {
                void follow(status.run_id)
            }
        }).catch(() => {})
    }, [])

    const start = (eventId: number | null) => {
        if (paymentEventId === null) return
        setProgress(null)
        setFailure(null)
        // The courier job must outlive the run rather than the request that starts it: Eko fetches
        // from Eventor throughout, and ending the job early would strand every fetch still to come.
        void courier.run(async () => {
            const { run_id } = await apiStartPrepaidCompetitionReconciliation(
                paymentEventId, eventId)
            await follow(run_id)
        })
    }

    const report = data?.report ?? null
    const findings: Finding[] = report === null ? [] : [
        ...report.reconciliation.over_invoiced.map((f): Finding => ({
            kind: 'over', clubs: [f.club], unit_price_ore: f.unit_price_ore, count: f.count,
            detail: `fakturerat: ${f.invoiced}, poster som nämner klubben: ${f.listed}`
                + (f.references.length > 0 ? ` · fakturor: ${f.references.join(', ')}` : ''),
        })),
        ...report.reconciliation.under_invoiced.map((f): Finding => ({
            kind: 'under', clubs: [f.club], unit_price_ore: f.unit_price_ore, count: f.count,
            detail: `fakturerat: ${f.invoiced}, `
                + `poster som bara klubben kan ha betalat: ${f.solo}`,
        })),
        ...report.reconciliation.groups.map((f): Finding => ({
            kind: 'group', clubs: f.clubs, unit_price_ore: f.unit_price_ore, count: f.net,
            detail: `klubbarna delar lag · fakturerat: ${f.invoiced}, anmält: ${f.registered}`,
        })),
    ].sort((a, b) => a.clubs[0].localeCompare(b.clubs[0], 'sv'))

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', gap: '0.75rem',
                      width: '100%', maxWidth: '60rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Button variant="outlined" disabled={running !== null || paymentEventId === null}
                        onClick={() => start(null)}>
                    {report !== null ? 'Uppdatera avstämningen' : 'Stäm av mot anmälningar'}
                </Button>
                {loading && progress === null &&
                    <span style={{ color: '#555' }}>Söker tävlingen …</span>}
            </div>

            {failure !== null && !loading &&
                <div style={{ color: '#c62828', border: '1px solid #c62828', borderRadius: 4,
                              padding: '0.5rem 0.75rem' }}>
                    {failure}
                </div>}

            {loading &&
                <div style={{ width: '100%' }}>
                    <LinearProgress
                        variant={progress === null ? 'indeterminate' : 'determinate'}
                        value={progress === null || progress.total === 0
                            ? 0 : 100 * progress.done / progress.total}/>
                    <div style={{ fontSize: '0.85em', color: '#555', marginTop: '0.25rem' }}>
                        {progress === null
                            ? 'Detta tar ett par minuter. Låt Eventor-fliken vara öppen.'
                            : `${PHASE[progress.phase] ?? progress.phase}: `
                              + `${progress.done} av ${progress.total}`}
                    </div>
                </div>}

            {/* A payment event usually belongs to one competition, but Eventor links the two only
                from the competition's side, so a competition run as several events is ambiguous. */}
            {data !== null && data.candidates.length !== 1 && report === null && !loading &&
                (data.candidates.length === 0
                    ? <p>Hittade ingen tävling som använder den här betalningsomgången.</p>
                    : <div>
                        <p>Flera tävlingar delar betalningsomgången. Vilken gäller?</p>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {data.candidates.map(id =>
                                <Button key={id} variant="outlined" onClick={() => start(id)}>
                                    Tävling {id}
                                </Button>)}
                        </div>
                      </div>)}

            {/* A run outlives the page that asked for it: a reload, a log out and in, or a change
                of role all find it again, and minutes or an hour may have passed. Eventor has
                moved on in the meantime, so the report is dated. */}
            {report !== null && when(data?.finished_at ?? null) !== null && !loading &&
                <div style={{ fontSize: '0.85em', color: '#555' }}>
                    Avstämningen gjordes {when(data?.finished_at ?? null)}.
                </div>}

            {report !== null && <>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead><TableRow>
                            <TableCell>Pris</TableCell>
                            <TableCell>Bokförs som</TableCell>
                            <TableCell align="right">Anmält/beställt</TableCell>
                            <TableCell align="right">Fakturerat</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {report.reconciliation.prices.map(p => {
                                const parts = data?.accounts[String(p.unit_price_ore)]?.parts
                                return (
                                    <TableRow key={p.unit_price_ore}>
                                        <TableCell>{ore(p.unit_price_ore)}</TableCell>
                                        {/* A price with no mapping is worth seeing: Eko could not
                                            book those rows either. A price split across several
                                            accounts shows the amount on each, since naming only
                                            the first would be half the truth. */}
                                        <TableCell style={{ color: parts?.length ? undefined : '#c62828' }}>
                                            {!parts?.length ? '— inget konto konfigurerat —'
                                             : parts.length === 1
                                                 ? `${parts[0].account_number} ${parts[0].account_name}`
                                                 : parts.map(part =>
                                                     <div key={part.account_number}>
                                                         {ore(part.amount_ore)}{' → '}
                                                         {part.account_number} {part.account_name}
                                                     </div>)}
                                        </TableCell>
                                        <TableCell align="right">{p.registered}</TableCell>
                                        <TableCell align="right">{p.invoiced}</TableCell>
                                    </TableRow>)
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {report.verified > 0 &&
                    <div style={{ fontSize: '0.85em', color: '#555' }}>
                        {/* Worth stating: it says whether the report is screening or checking. The
                            club page's fee is cheap to read and goes stale when a deadline moves,
                            so anything that would be reported is checked on the team's own page. */}
                        Kontrollerade {report.verified} lagavgifter mot lagsidorna.
                    </div>}

                {findings.length === 0
                    ? <p>Inga avvikelser: alla klubbar har det de är fakturerade för.</p>
                    : <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell>Klubb</TableCell>
                                <TableCell>Avvikelse</TableCell>
                                <TableCell>Pris</TableCell>
                                <TableCell align="right">Antal</TableCell>
                                <TableCell>Underlag</TableCell>
                                <TableCell/>
                            </TableRow></TableHead>
                            <TableBody>
                                {findings.map((f, i) =>
                                    <TableRow key={i}>
                                        <TableCell>{f.clubs.join(' + ')}</TableCell>
                                        <TableCell style={{ color: KIND[f.kind].color }}>
                                            {KIND[f.kind].label}
                                        </TableCell>
                                        <TableCell>{ore(f.unit_price_ore)}</TableCell>
                                        <TableCell align="right">{f.count}</TableCell>
                                        <TableCell style={{ fontSize: '0.85em', color: '#555' }}>
                                            {f.detail}
                                        </TableCell>
                                        <TableCell>
                                            {canCorrect && servicesOf(report.evidence, f) !== null &&
                                                <Button size="small"
                                                        onClick={() => onCorrectClub(
                                                            servicesOf(report.evidence, f)!)}>
                                                    Rätta tjänster
                                                </Button>}
                                        </TableCell>
                                    </TableRow>)}
                            </TableBody>
                        </Table>
                      </TableContainer>}

                {report.unordered_services.length > 0 &&
                    <div>
                        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                            Sparat men inte beställt
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#555', marginBottom: '0.5rem' }}>
                            Tjänsterna syns inte i anmälningsöversikten och faktureras nästa gång
                            någon beställer något för klubben. De ingår inte i avstämningen ovan.
                        </div>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead><TableRow>
                                    <TableCell>Klubb</TableCell>
                                    <TableCell>Tjänster</TableCell>
                                    <TableCell/>
                                </TableRow></TableHead>
                                <TableBody>
                                    {report.unordered_services.map(entry =>
                                        <TableRow key={entry.organisation_id}>
                                            <TableCell>{entry.club}</TableCell>
                                            <TableCell style={{ fontSize: '0.9em' }}>
                                                {entry.services.join(', ')}
                                            </TableCell>
                                            <TableCell>
                                                {canCorrect &&
                                                    <Button size="small"
                                                            onClick={() => onCorrectClub(
                                                                { id: entry.organisation_id,
                                                                  name: entry.club })}>
                                                        Visa tjänster
                                                    </Button>}
                                            </TableCell>
                                        </TableRow>)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>}

                {report.evidence.map((club: ClubEvidence) =>
                    <div key={club.club} style={{ fontSize: '0.85em' }}>
                        <strong>{club.club}</strong>
                        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ color: '#555' }}>Anmält/beställt</div>
                                {club.registrations.length === 0 ? <div>–</div> :
                                    club.registrations.map((r, i) =>
                                        <div key={i}>
                                            {r.description} · {ore(r.unit_price_ore)}
                                            {r.count !== 1 && ` × ${r.count}`}
                                            {r.shared_with.length > 0 &&
                                                ` · delas med ${r.shared_with.join(', ')}`}
                                        </div>)}
                            </div>
                            <div>
                                <div style={{ color: '#555' }}>Fakturerat</div>
                                {club.invoice_rows.length === 0 ? <div>–</div> :
                                    club.invoice_rows.map((r, i) =>
                                        <div key={i}>
                                            {r.reference} · {r.quantity} × {ore(r.unit_price_ore)}
                                        </div>)}
                            </div>
                        </div>
                    </div>)}

                <p style={{ fontSize: '0.85em', color: '#555' }}>
                    Läses så: <em>för mycket fakturerat</em> gäller även om varje post
                    som nämner klubben vore klubbens egen; <em>för lite fakturerat</em> gäller
                    poster som ingen annan klubb kan ha betalat. En post är ett lag eller en
                    beställd tjänst — priset är det som skiljer dem åt. En klubb som står med på två priser, med för mycket på det
                    ena och för lite på det andra, har oftast fått sina lag omprissatta av en flyttad
                    anmälningsdeadline. Lagnamn jämförs aldrig — Eventor byter namn på ett lag när en
                    klubb går med och numrerar om när ett lag tas bort.
                </p>
            </>}
        </div>
    )
}

export default PrepaidCompetitionReconciliation
